import { normalizeError } from '@/shared/api'

/**
 * Что показать на отказ выдачи каталога (`GET /v1/catalog/products`). Конфликтующих отборов и
 * недоступной сортировки фронт не формирует (`withSelection` снимает прежний отбор, ключи
 * сортировки — по виду), но бэк отвечает на них кодами контракта — ветки защитные, на случай
 * устаревшей ссылки или адреса, набранного руками.
 *
 * - `pair-gone` — `404` при фильтре узла: пара «свойство — значение» пропала из узла
 *   (`message = property.not-found`); распознаётся по `param` в срезе, как и в старом коде.
 * - `sort-unavailable` — `400 fields.sort = "sort.unavailable"`: ключ сортировки недоступен в
 *   этом виде (полный вид менеджера — `price`/`available`; свой вид клиента со скрытым остатком —
 *   `available`).
 * - `conflict` — `400` взаимоисключающих отборов и неполного фильтра: `reference.exclusive`,
 *   `search.exclusive`, `property.incomplete`, `property.category-required`.
 * - `generic` — прочее: сеть, `5xx`, неизвестный код.
 */
export type CatalogListErrorKind = 'pair-gone' | 'sort-unavailable' | 'conflict' | 'generic'

/** Коды поля `fields.<младший>` у `400`, на которые у выдачи своя ветка «Отборы конфликтуют». */
const CONFLICT_CODES = new Set<string>([
  'reference.exclusive',
  'search.exclusive',
  'property.incomplete',
  'property.category-required',
])

/**
 * Классификация отказа выдачи. `hasPropertyFilter` — в срезе есть фильтр узла (`param`): без него
 * `404` — это модификатор (клиент не найден и т. п.), его разбирает экран, а не выдача.
 */
export function catalogListError(error: unknown, hasPropertyFilter: boolean): CatalogListErrorKind {
  const failure = normalizeError(error)
  if (failure.status === 404 && hasPropertyFilter) return 'pair-gone'
  if (failure.status === 400 && failure.fields !== undefined) {
    const codes = Object.values(failure.fields).map((field) => field.code)
    if (codes.includes('sort.unavailable')) return 'sort-unavailable'
    if (codes.some((code) => CONFLICT_CODES.has(code))) return 'conflict'
  }
  return 'generic'
}
